/** Generic renderer for every generation/intelligence/output ACTION node
 * (Generate Text/Image/Voice/Keyframe/Video, Lip Sync, Edit/Composite,
 * Analyze Content, Generate Production Plan, Check/Optimize, Auto
 * Complete, Platform Adaptation, Publish). Deliberately renders a
 * "运行" affordance as disabled/staged — spec §12 forbids implementing
 * real Codex/Skill execution in this task; the UI contract (skill_id/
 * run history surface) is CONTRACT_READY, not wired.
 *
 * `generate_text` only (VISTOERA_CONTENT_CANVAS_MEDIA_NODE_HOVER_ACTIONS_CONNECTION_PORTS_POPOVER_AND_PLACEMENT_P1
 * §13/§14/§39) reuses this SAME shell rather than a bespoke node type —
 * no canonical AI text/image-analysis execution exists yet
 * (`TEXT_EXECUTION_AVAILABLE = NO`, see this round's final report), so
 * the Contextual Creation Rail's "生成文本" button creates exactly this
 * generic shell + a real image-context edge, never a fake completed
 * result. Its target Handle carries the SAME `image-ref-in` id
 * `generate_image`/`generate_video` already use (one canonical "image
 * reference in" contract, spec §14 "不要...临时造一个假 backend") so
 * `CanvasBoard`'s existing reference-edge validator/derivation covers
 * it with zero special-casing beyond this file. */

import { Handle, Position, type NodeProps } from '@xyflow/react'
import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import { GENERIC_INPUT_HANDLE } from '../dataflow'
import { useNodeDefinition } from '../nodeRegistry'
import { useCanvasNodeRegistryI18n } from '../nodeRegistryI18n'
import InputMaterialsRow from './InputMaterialsRow'
import type { FlowNodeData } from '../types'

const IMAGE_REF_HANDLE_TYPES = new Set(['generate_text'])

function ActionNodeImpl({ id, data, selected }: NodeProps) {
  const { t } = useTranslation('canvas')
  const { nodeInstanceLabel } = useCanvasNodeRegistryI18n()
  const nodeData = data as unknown as FlowNodeData & { workspaceId?: string }
  const def = useNodeDefinition(nodeData.nodeType)
  const label = nodeInstanceLabel(nodeData.nodeType, nodeData.fields?.label as string | undefined, def.label)
  const usesImageRefHandle = IMAGE_REF_HANDLE_TYPES.has(nodeData.nodeType)
  // Unified Input Materials (P0 §7/§12) — replaces the old read-only
  // `ImageContextThumb`-only preload with the SAME `InputMaterialsRow`
  // every other Generation Node now uses (image/text/video, disconnect
  // + "查看来源" + "编辑源素材" included) — `generate_text` had no
  // "断开" affordance at all before this round, only a passive thumbnail.
  const inputMaterials = usesImageRefHandle ? nodeData.inputMaterials || [] : []

  return (
    <div className={`pcv-node pcv-node--action${selected ? ' is-selected' : ''}`} style={{ borderColor: selected ? def.color : undefined }}>
      <Handle type="target" position={Position.Left} id={usesImageRefHandle ? GENERIC_INPUT_HANDLE : undefined} className="pcv-handle" />
      <div className="pcv-node-header">
        <span className="pcv-node-icon pcv-node-icon--action" style={{ color: def.color, background: `${def.color}22` }}>
          {def.icon}
        </span>
        <span className="pcv-node-title" title={label}>
          {label}
        </span>
      </div>
      {inputMaterials.length > 0 ? (
        <InputMaterialsRow
          workspaceId={nodeData.workspaceId || ''}
          materials={inputMaterials}
          onDisconnect={(edgeId) => nodeData.onDisconnectMaterial?.(id, edgeId)}
          onSelectSource={(nodeId) => nodeData.onSelectNode?.(nodeId)}
          onEditText={(nodeId) => nodeData.onRequestEditText?.(nodeId)}
        />
      ) : null}
      <div className="pcv-node-run-row">
        <span className="pcv-run-status pcv-run-status--idle">
          {nodeData.latestRunId
            ? t('action.runCompleted')
            : usesImageRefHandle
              ? t('action.awaitingInput')
              : t('action.notRun')}
        </span>
        <button type="button" className="pcv-run-btn" disabled title={t('action.runDisabledTooltip')}>
          {t('action.run')}
        </button>
      </div>
      {/* Output Handle id deliberately left unset (§48 backward
       * compatibility) — any pre-existing persisted edge sourced from a
       * `generate_text` node was created with `sourceHandle: null`
       * (React Flow's single-default-handle convention); introducing a
       * NEW named id here (`text-out`) would silently break that old
       * edge's on-screen rendering (DOM handle id would no longer match
       * the persisted edge's `sourceHandle`). `getNodeIO('generate_text')`
       * still reports a `text-out` OUTPUT PORT for typed-validation
       * purposes — that's a pure data-model concept and never needs to
       * match this DOM id, since `validateTypedConnection` only compares
       * NODE TYPES, never handle ids. */}
      <Handle type="source" position={Position.Right} className="pcv-handle" />
    </div>
  )
}

export default memo(ActionNodeImpl)
